import { useTranslation } from 'react-i18next';
import { changeCountry } from '../../sagas/sagaActions';
import { type BillingForm, setForm } from '../../store/billingSlice';
import { useAppDispatch } from '../../store/hooks';
import type { Country } from '../../types/country';
import styles from '../checkoutLayout.module.scss';
import { ChevronDownIcon } from '../icons';

function TextField({
  label,
  hasError,
  value,
  onChange,
}: {
  label: string;
  hasError?: boolean;
  value?: string;
  onChange?: (value: string) => void;
}) {
  return (
    <label className={styles.formField}>
      <span className={styles.formLabel}>{label}</span>
      <div className={styles.control}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          className={`${styles.controlBox} ${hasError ? styles.error : ''}`}
        />
      </div>
    </label>
  );
}

export function Billing({
  form,
  fieldErrors,
  countries,
}: {
  form: BillingForm;
  fieldErrors: Record<string, string | null>;
  countries: Country[] | null;
}) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  return (
    <div className={styles.group20}>
      <div className={styles.group10}>
        <TextField
          label={t('First name')}
          value={form.firstName}
          hasError={Boolean(fieldErrors.firstName)}
          onChange={(value) => dispatch(setForm({ firstName: value }))}
        />
        <TextField
          label={t('Last name')}
          value={form.lastName}
          hasError={Boolean(fieldErrors.lastName)}
          onChange={(value) => dispatch(setForm({ lastName: value }))}
        />
        <TextField
          label={t('Address')}
          value={form.address}
          hasError={Boolean(fieldErrors.address)}
          onChange={(value) => dispatch(setForm({ address: value }))}
        />
        <div className={styles.row}>
          <div className={styles.grow}>
            <TextField
              label={t('City')}
              value={form.city}
              hasError={Boolean(fieldErrors.city)}
              onChange={(value) => dispatch(setForm({ city: value }))}
            />
          </div>
          <div className={styles.wZip}>
            <TextField
              label={t('Zip code')}
              value={form.zip}
              hasError={Boolean(fieldErrors.zip)}
              onChange={(value) => dispatch(setForm({ zip: value }))}
            />
          </div>
        </div>
        <label className={styles.formField}>
          <span className={styles.formLabel}>{t('Country or region')}</span>
          <div className={styles.control}>
            <select
              className={`${styles.controlBox} ${styles.selectBox} ${styles.controlBoxIcon} ${styles.nativeSelect}`}
              value={form.country}
              onChange={(e) => dispatch(changeCountry(e.target.value))}
            >
              <option value="" disabled>
                {t('Please select')}
              </option>
              {countries?.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              className={`${styles.selectChevron} ${styles.selectChevronOverlay}`}
            />
          </div>
        </label>
      </div>

      <div className={styles.group10}>
        <div className={styles.optional}>{t('Optional')}</div>
        <div className={styles.row}>
          <div className={styles.grow}>
            <TextField
              label={t('VAT ID')}
              hasError={Boolean(fieldErrors.vat)}
              value={form.vat}
              onChange={(value) => dispatch(setForm({ vat: value }))}
            />
          </div>
          <div className={styles.grow}>
            <TextField
              label={t('Company')}
              value={form.company}
              onChange={(value) => dispatch(setForm({ company: value }))}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
